import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from "@stripe/react-stripe-js";
import React from "react";

import { setNonceAndDeviceData, useCartStateDispatch } from "../cartSlice";
import type { CartCheckoutProps } from "../cart-types";

import { PaymentButtons, progressToSubmit } from "./shared";
import type { SubmitCheckoutFn } from "./shared";

type StripePaymentHeaderComponentProps = {
  isPayment: boolean;
};
function StripePaymentHeader({ isPayment }: StripePaymentHeaderComponentProps) {
  if (!isPayment) return null;

  return (
    <>
      <div className="d-flex flex-column justify-content-between">
        <strong>Payment Details</strong>
      </div>
      <div>
        On the next page, you will be able to review your order and enter your
        payment details.
      </div>
    </>
  );
}

interface StripePaymentUiProps extends Pick<PaymentProps, "checkout"> {
  stripe: ReturnType<typeof loadStripe>;
}
function StripePaymentUi({ checkout, stripe }: StripePaymentUiProps) {
  if (checkout == null) return null;
  const options = { clientSecret: checkout.clientToken };
  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider stripe={stripe} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}

interface PaymentProps extends CartCheckoutProps {
  submitCheckout: SubmitCheckoutFn;
}

export default function PaymentStripeGateway({
  checkout,
  state,
  submitCheckout
}: PaymentProps) {
  const dispatch = useCartStateDispatch();
  const [stripePromise, setStripePromise] = React.useState<ReturnType<
    typeof loadStripe
  > | null>(null);
  React.useEffect(() => {
    // To reuse the existing API, we put the stripe public key in the merchantId
    if (checkout.merchantId == null) return;
    setStripePromise(loadStripe(checkout.merchantId));
  }, [checkout.merchantId]);

  const isPayment = state.status.step === 2;
  const onSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const nonce = checkout.clientToken;
      const deviceData = ""; // Stripe does not require device data
      dispatch(setNonceAndDeviceData({ nonce, deviceData }));
    },
    [checkout, dispatch]
  );
  React.useEffect(() => {
    if (state.nonce == null) return;
    progressToSubmit(dispatch, state, checkout, submitCheckout);
  }, [checkout, dispatch, state, submitCheckout]);

  if (checkout == null) return null;
  return (
    <form onSubmit={onSubmit}>
      {isPayment && stripePromise != null ? (
        <StripePaymentUi checkout={checkout} stripe={stripePromise} />
      ) : (
        <>
          <StripePaymentHeader isPayment={isPayment} />
          <PaymentButtons isRequestingPaymentMethod={state.isRequestingNonce} />
        </>
      )}
    </form>
  );
}
